// ─────────────────────────────────────────────────────────────────────
// KLUBBMESTERSKAP (KM) RESULT / ELO / STANDINGS / MUTATION HELPERS
//
// The KM-specific result, chronological-ELO, standings, knockout-rebuild and
// group-mutation helpers, lifted verbatim out of legacy/App.jsx (PR-AT) as
// part of decommissioning the monolith. They were threaded into the KM views
// as props; they now live in the engine layer and are imported directly.
//
// Engine bindings (identity-safe — see PR-AS / the platformEngine wiring):
//   - calcEloChange / kmMatchMov come from ./elo (static refs, not getters).
//   - calcGroupStandings / determineBracketStructure /
//     buildDivisionKnockoutMatches + the KM_DEFAULT_* consts come from ./km.
// The original `typeof Padel24Engine !== 'undefined'` guards in
// rebuildKnockoutForDivision / propagateKMResults were defensive against an
// impossible runtime state (Padel24Engine is always defined); with the
// primitives imported directly those checks are always-false, so they are
// dropped — runtime behaviour is identical.
//
// NOTE: propagateKMResults MUTATES tournament.matches in place and returns
// void (callers rely on the mutation) — preserved verbatim.
// ─────────────────────────────────────────────────────────────────────
import { calcEloChange, kmMatchMov } from './elo.js';
import { EngineSettings, type EngineSettingsValues } from './constants.js';
import { resolveEffects, type EngineEffects } from './effects.js';
import {
  calcGroupStandings,
  determineBracketStructure,
  buildDivisionKnockoutMatches,
  KM_DEFAULT_GROUP_MATCH_FORMAT,
  KM_DEFAULT_KNOCKOUT_MINUTES,
  scheduleKMMatches,
} from './km.js';

export function computeKMClassStandings(tournament: any, divisionId: any) {
  const pairs = (tournament.pairs || []).filter((p) => p.divisionId === divisionId);
  if (pairs.length === 0) return [];
  const pairById = new Map(pairs.map((p) => [p.id, p]));
  const divMatches = (tournament.matches || []).filter(
    (m) =>
      m.divisionId === divisionId &&
      m.score &&
      pairById.has(m.team1?.pairId) &&
      pairById.has(m.team2?.pairId),
  );

  const stats = new Map(); // pairId → { played, wins, losses, eloPerf }
  pairs.forEach((p) => stats.set(p.id, { played: 0, wins: 0, losses: 0, eloPerf: 0 }));
  divMatches.forEach((m) => {
    const s = m.score;
    const reg = (pairId, won, change) => {
      const st = stats.get(pairId);
      if (!st) return;
      st.played += 1;
      if (won) st.wins += 1;
      else st.losses += 1;
      if (change != null) st.eloPerf += change;
    };
    reg(m.team1.pairId, s.t1 > s.t2, m.teamEloChange?.t1);
    reg(m.team2.pairId, s.t2 > s.t1, m.teamEloChange?.t2);
  });

  const rows = pairs.map((p) => {
    const st = stats.get(p.id) || { played: 0, wins: 0, losses: 0, eloPerf: 0 };
    return {
      pairId: p.id,
      p1Id: p.p1Id,
      p2Id: p.p2Id,
      p1Name: p.p1Name,
      p2Name: p.p2Name,
      played: st.played,
      wins: st.wins,
      losses: st.losses,
      eloPerf: Math.round(st.eloPerf),
    };
  });

  // ---- Bracket-aware placement ----
  // The knockout stage decides the top placements: A-final (1st/2nd), bronze
  // (3rd/4th), then how far you got in A. The B knockout is ranked AFTER A, and
  // pairs that only played the group stage come dead last.
  const koMatches = (tournament.matches || []).filter(
    (m) =>
      m.divisionId === divisionId &&
      m.phase === 'knockout' &&
      m.score &&
      pairById.has(m.team1?.pairId) &&
      pairById.has(m.team2?.pairId),
  );
  const resultOf = (m) => {
    if (!m?.score) return null;
    const t1Win = m.score.t1 > m.score.t2;
    return {
      w: t1Win ? m.team1.pairId : m.team2.pairId,
      l: t1Win ? m.team2.pairId : m.team1.pairId,
    };
  };
  const RW = {
    f: 6,
    sf: 5,
    qf: 4,
    r16: 3,
    r32: 2,
    bronze: 5,
    b_f: 6,
    b_sf: 5,
    b_qf: 4,
    b_r16: 3,
    b_r32: 2,
    b_bronze: 5,
  };
  const isA = (m) => m.bracket === 'A' || !m.bracket;
  const find = (pred) => koMatches.find(pred);
  const aF = find((m) => isA(m) && m.round === 'f');
  const aBr = find((m) => isA(m) && m.round === 'bronze');
  const bF = find((m) => m.bracket === 'B' && m.round === 'b_f');
  const bBr = find((m) => m.bracket === 'B' && m.round === 'b_bronze');
  // Group standings are used to rank pairs that didn't reach the knockout + as a tiebreak.
  const groupStandingsByPair = new Map();
  (tournament.groups || [])
    .filter((g) => g.divisionId === divisionId)
    .forEach((g) => {
      const gPairs = pairs.filter((p) => p.groupId === g.id && p.divisionId === divisionId);
      const gMatches = (tournament.matches || []).filter(
        (m) => m.phase === 'group' && m.groupId === g.id && m.divisionId === divisionId,
      );
      (calcGroupStandings(gPairs, gMatches) || []).forEach((s) =>
        groupStandingsByPair.set(s.pair.id, s.pos),
      );
    });

  const finishValue = (pid) => {
    const aMs = koMatches.filter(
      (m) => isA(m) && (m.team1?.pairId === pid || m.team2?.pairId === pid),
    );
    const bMs = koMatches.filter(
      (m) => m.bracket === 'B' && (m.team1?.pairId === pid || m.team2?.pairId === pid),
    );
    if (aMs.length) {
      if (aF && resultOf(aF)?.w === pid) return 3000; // A-champion
      if (aF && resultOf(aF)?.l === pid) return 2900; // A-final loser → 2nd place
      if (aBr && resultOf(aBr)?.w === pid) return 2800; // bronze winner → 3rd
      if (aBr && resultOf(aBr)?.l === pid) return 2700; // bronze loser → 4th
      return 2000 + Math.max(...aMs.map((m) => RW[m.round] || 1)); // otherwise: depth in A
    }
    if (bMs.length) {
      if (bF && resultOf(bF)?.w === pid) return 1300; // B-champion
      if (bF && resultOf(bF)?.l === pid) return 1290;
      if (bBr && resultOf(bBr)?.w === pid) return 1280;
      if (bBr && resultOf(bBr)?.l === pid) return 1270;
      return 1000 + Math.max(...bMs.map((m) => RW[m.round] || 1));
    }
    // Group stage only — ranked by group placement (lower pos = better).
    const gp = groupStandingsByPair.get(pid);
    return gp != null ? 500 - gp : 0;
  };
  rows.forEach((r) => {
    r._fin = finishValue(r.pairId);
    r._grp = groupStandingsByPair.get(r.pairId) ?? 99;
  });

  rows.sort(
    (a, b) =>
      b._fin - a._fin ||
      a._grp - b._grp ||
      b.wins - a.wins ||
      b.eloPerf - a.eloPerf ||
      a.losses - b.losses ||
      b.played - a.played ||
      (a.p1Name || '').localeCompare(b.p1Name || ''),
  );
  rows.forEach((r, i) => {
    r.pos = i + 1;
    delete r._fin;
    delete r._grp;
  });
  return rows;
}

/** Which step of applyKMResult failed. */
export type KMApplyStep = 'baseline' | 'elo-recalc' | 'propagation';

export interface KMApplyIssue {
  step: KMApplyStep;
  cause: unknown;
}

/** Thrown by the DEFAULT issue handler — i.e. when nobody has chosen otherwise. */
export class KMApplyError extends Error {
  readonly step: KMApplyStep;
  /**
   * The failure, in full.
   *
   * Deliberately NOT called `cause`. That name is `Error.cause`, which exists
   * in this package's lib target but not in every consumer's — so shadowing it
   * needs an `override` modifier in one package and is refused in the other.
   * Carrying the whole issue sidesteps the divergence and tells a caller the
   * same thing.
   */
  readonly issue: KMApplyIssue;
  constructor(issue: KMApplyIssue) {
    const detail = issue.cause instanceof Error ? issue.cause.message : String(issue.cause);
    super(`[KM] ${issue.step} failed: ${detail}`);
    this.name = 'KMApplyError';
    this.step = issue.step;
    this.issue = issue;
  }
}

export type KMIssueHandler = (issue: KMApplyIssue) => void;

/**
 * FAIL LOUDLY. The default, because a silent partial application is the worst
 * of the available outcomes: the score is written, the standings are wrong, and
 * the caller was told it worked.
 */
export const throwOnKMIssue: KMIssueHandler = (issue) => {
  throw new KMApplyError(issue);
};

/**
 * DEGRADE, and say so. For the browser, where a person is looking at the board
 * and a half-applied result is better than a blank screen mid-tournament.
 * Call-sites choose this deliberately; nothing gets it by default.
 */
export const warnOnKMIssue: KMIssueHandler = (issue) => {
  console.warn(`[KM] ${issue.step} feilet:`, issue.cause);
};

// ALREADY DETERMINISTIC (#203): this reads no clock and mints no id. The caller
// builds `updatedMatch` — including its completedAt — and the recalc it runs
// (recalcKMElo) replays the division chronologically through calcEloChange,
// which takes no `now` and applies no newbie boost. So KM results do not drift
// with when they are applied, and this needs no effects parameter.
//
// ERROR POLICY IS THE CALLER'S (#204). This used to protect its later steps and
// not its first, so identical malformed input failed LOUDLY or SILENTLY
// depending only on whether a baseline already existed:
//
//   playerBaseElo capture  threw straight to the caller
//   recalcKMElo            console.warn, result returned anyway
//   propagateKMResults     console.warn, result returned anyway
//
// A caller could not tell a fully-applied result from a partial one. Tolerable
// in a browser where somebody is watching the board; not in an API, where the
// pad gets a 200 and nobody reads the server log.
//
// All three steps are now handled IDENTICALLY, and what "handled" means is
// injected. The default throws, so a new call-site fails loudly until someone
// decides degradation is acceptable there — which is the right way round.
export function applyKMResult(
  tournament: any,
  updatedMatch: any,
  allPlayers: any = [],
  options: { onIssue?: KMIssueHandler } = {},
  effects?: EngineEffects,
) {
  const onIssue = options.onIssue ?? throwOnKMIssue;
  // Resolved ONCE for the whole application, so every rating computed below
  // uses the same configuration (#243).
  const { settings } = resolveEffects(effects);
  const newMatches = tournament.matches.map((m) => (m.id === updatedMatch.id ? updatedMatch : m));
  let newTournament = { ...tournament, matches: newMatches };

  // Store base ELO on the first result, so the chronological recalc always
  // starts from the same baseline (the players' ELO at tournament start).
  // `playerBaseElo` IS NO LONGER CAPTURED (#249).
  //
  // It froze each player's rating at the first result and then decided the
  // whole season's calculations, so a player who improved was still measured
  // from where they started. The rule is now the player's current
  // authoritative rating at the moment each result is recorded.
  //
  // Existing tournaments keep the field: it is historical data, and
  // `SeriespillPlayerModal` still reads it for the season's starting point
  // (falling back to `playerElo − eloDelta` where it is absent).

  // Chronological ELO recalc: updates playerEloPre/teamEloChange/teamEloPost
  // on all played matches. A team that lost earlier has lower ELO in its
  // next match (chronology), and we use the team average as everywhere else in the system.
  try {
    const { matches: eloMatches } = recalcKMElo(newTournament, allPlayers, settings);
    newTournament = { ...newTournament, matches: eloMatches };
  } catch (cause) {
    onIssue({ step: 'elo-recalc', cause });
  }

  // After the score update: try to propagate group-stage results to the knockout stage
  // (when a group has finished playing, fill the group winners into the KO placeholders)
  try {
    propagateKMResults(newTournament);
  } catch (cause) {
    onIssue({ step: 'propagation', cause });
  }

  return newTournament;
}

export function rebuildKnockoutForDivision(tournament: any, divisionId: any) {
  if (!tournament) return tournament;
  let divCfg = null,
    clsCfg = null;
  (tournament.config?.classes || []).forEach((cls) => {
    (cls.divisions || []).forEach((d) => {
      if (d.id === divisionId) {
        divCfg = d;
        clsCfg = cls;
      }
    });
  });
  if (!divCfg || !clsCfg) return tournament;

  // Groups with pairs (division-aware)
  const divGroups = tournament.groups
    .filter((g) => g.divisionId === divisionId)
    .map((g) => ({
      ...g,
      pairs: tournament.pairs.filter((p) => p.groupId === g.id && p.divisionId === divisionId),
    }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  if (divGroups.length === 0) return tournament;

  const pairCount = divGroups.reduce((s, g) => s + g.pairs.length, 0);
  const groupCount = divGroups.length;
  // A single group keeps its group stage too: the operator may have forced a
  // final / semi-finals on top of the round robin (knockoutOverride).
  const useGroupStage = divCfg.useGroupStage !== false && groupCount >= 1;

  const struct = useGroupStage
    ? determineBracketStructure(groupCount, pairCount, divCfg.knockoutOverride || 'auto')
    : { advanceTopN: divCfg.advanceTopN || 2, bracketSize: 0, luckyLosers: 0 };
  // The B knockout can have its own format (independent of A)
  const bStruct =
    useGroupStage && divCfg.consolationBracket
      ? determineBracketStructure(
          groupCount,
          pairCount,
          divCfg.bKnockoutOverride || divCfg.knockoutOverride || 'auto',
        )
      : null;

  const divisionObj = {
    id: divCfg.id,
    classId: clsCfg.id,
    useGroupStage,
    advanceTopN: struct.advanceTopN,
    luckyLosers: struct.luckyLosers,
    bracketSize: struct.bracketSize,
    consolationBracket: divCfg.consolationBracket === true,
    bronzeFinal: divCfg.bronzeFinal,
    bBronzeFinal: divCfg.bBronzeFinal,
    bAdvanceTopN: bStruct ? bStruct.advanceTopN : struct.advanceTopN,
    bBracketSize: bStruct ? bStruct.bracketSize : struct.bracketSize,
    knockoutMatchFormat: divCfg.knockoutMatchFormat || KM_DEFAULT_GROUP_MATCH_FORMAT,
    knockoutMinutes: divCfg.knockoutMinutes || KM_DEFAULT_KNOCKOUT_MINUTES,
  };

  // Update the config division with fresh derived values (for display/propagation)
  const newClasses = (tournament.config.classes || []).map((cls) => ({
    ...cls,
    divisions: (cls.divisions || []).map((d) =>
      d.id === divisionId
        ? {
            ...d,
            advanceTopN: struct.advanceTopN,
            luckyLosers: struct.luckyLosers,
            bracketSize: struct.bracketSize,
          }
        : d,
    ),
  }));

  const newKo = buildDivisionKnockoutMatches(divisionObj, divGroups);
  // Remove old KO matches for the division, keep the group stage + other divisions
  const remaining = tournament.matches.filter(
    (m) => !(m.phase === 'knockout' && m.divisionId === divisionId),
  );
  return {
    ...tournament,
    config: { ...tournament.config, classes: newClasses },
    matches: [...remaining, ...newKo],
  };
}

// ── A SETUP EDIT KEEPS THE KNOCKOUT CHOICES IT RESTORES (#882) ───────
//
// Editing a KM's setup before any result re-creates the tournament through
// `createKlubbmesterskapTournament`, which knows nothing about the choices made
// afterwards on the knockout panel. The edit path therefore carries them over
// per division (matched on class id + class name + division name), for every
// division where the admin had confirmed a knockout format (`knockoutChosen`).
//
// TWO DEFECTS THIS FIXES, both «the screen and the generator disagree»:
//
//  1. The bronze choice was carried under `hasBronze`, a key nothing writes.
//     The panel and the generator both use `bronzeFinal` (and `bBronzeFinal`
//     for the B bracket), so an edit silently dropped a ticked bronze final.
//     They are carried now — only when the old division stored one, so a
//     division that never chose keeps the default, which is OFF.
//  2. The carried choices were written into the config but the knockout
//     MATCHES were the ones the fresh setup built, so the panel could show
//     «Kvartfinaler» or a ticked bronze box over a bracket that had neither.
//     A division whose carried choices differ from what it was built with now
//     has its knockout rebuilt, and the calendar is rescheduled once.
//
// `edited` is the freshly created tournament and is not mutated; the returned
// object is new. Nothing here runs once results exist — the caller only uses it
// for an edit «without results».
const CARRIED_KNOCKOUT_KEYS = [
  'knockoutOverride',
  'bKnockoutOverride',
  'bronzeFinal',
  'bBronzeFinal',
] as const;

// What a key MEANS to the generator: an override is «unset» whether it is null or
// missing, and a bronze final is on only when it is exactly `true`.
const effectiveKnockoutChoice = (key: string, value: any) =>
  key === 'bronzeFinal' || key === 'bBronzeFinal' ? value === true : (value ?? null);

export function carryKnockoutChoicesIntoEdit(previous: any, edited: any) {
  const keyOf = (cls: any, div: any) => `${cls.id}__${cls.name}__${div.name}`;
  const chosen = new Map<string, any>();
  (previous?.config?.classes || []).forEach((c: any) =>
    (c.divisions || []).forEach((d: any) => {
      if (!d.knockoutChosen) return;
      const carried: any = {
        knockoutOverride: d.knockoutOverride ?? null,
        knockoutChosen: true,
        bKnockoutOverride: d.bKnockoutOverride ?? null,
        // Carried verbatim from the monolith: nothing writes `hasBBracket`
        // (the B bracket is `consolationBracket`), so this is always undefined.
        hasBBracket: d.hasBBracket,
      };
      if (d.bronzeFinal !== undefined) carried.bronzeFinal = d.bronzeFinal;
      if (d.bBronzeFinal !== undefined) carried.bBronzeFinal = d.bBronzeFinal;
      chosen.set(keyOf(c, d), carried);
    }),
  );

  const toRebuild: string[] = [];
  const classes = (edited?.config?.classes || []).map((c: any) => ({
    ...c,
    divisions: (c.divisions || []).map((d: any) => {
      const prev = chosen.get(keyOf(c, d));
      if (!prev) return d;
      const merged = { ...d, ...prev };
      const differs = CARRIED_KNOCKOUT_KEYS.some(
        (k) => effectiveKnockoutChoice(k, merged[k]) !== effectiveKnockoutChoice(k, d[k]),
      );
      if (differs) toRebuild.push(d.id);
      return merged;
    }),
  }));

  let out = { ...edited, config: { ...edited.config, classes } };
  if (toRebuild.length === 0) return out;
  out = { ...out, matches: (out.matches || []).map((m: any) => ({ ...m })) };
  for (const divisionId of toRebuild) out = rebuildKnockoutForDivision(out, divisionId);
  scheduleKMMatches(out);
  return out;
}

export function buildGroupMatches(group: any, effects?: EngineEffects) {
  // The ids here carry a timestamp and a random suffix, so a retried rebuild
  // used to mint DIFFERENT ids for the same fixtures and leave duplicates
  // instead of converging (#203). Supplying `effects` makes a rebuild
  // reproducible; omitting it keeps the historical shape exactly.
  //
  // Contrast buildDivisionKnockoutMatches in ./km, whose ids are derived purely
  // from the division, round and slot. That is the shape these should
  // eventually take; changing them now would rename ids already persisted in
  // every stored tournament.
  const { now, uniqueSuffix } = resolveEffects(effects);
  const pairs = group.pairs;
  const out = [];
  let counter = 0;
  for (let i = 0; i < pairs.length; i++) {
    for (let j = i + 1; j < pairs.length; j++) {
      counter++;
      out.push({
        id: `km_match_${group.id}_r${counter}_${now}_${uniqueSuffix()}`,
        classId: group.classId,
        divisionId: group.divisionId,
        groupId: group.id,
        groupName: group.name,
        phase: 'group',
        roundIndex: 1,
        team1: { pairId: pairs[i].id },
        team2: { pairId: pairs[j].id },
        minutesPerMatch: 60,
      });
    }
  }
  return out;
}

export function swapPairsInTournament(tournament: any, pairA: any, pairB: any) {
  if (!tournament || !pairA || !pairB) return null;
  if (pairA.id === pairB.id || pairA.groupId === pairB.groupId) return null;
  if (!pairA.groupId || !pairB.groupId) return null;

  const aGroup = tournament.groups.find((g) => g.id === pairA.groupId);
  const bGroup = tournament.groups.find((g) => g.id === pairB.groupId);
  if (!aGroup || !bGroup) return null;

  // Swap groups — the pair inherits the target group's class/division (cross-class OK)
  const newPairs = tournament.pairs.map((p) => {
    if (p.id === pairA.id)
      return { ...p, groupId: bGroup.id, divisionId: bGroup.divisionId, classId: bGroup.classId };
    if (p.id === pairB.id)
      return { ...p, groupId: aGroup.id, divisionId: aGroup.divisionId, classId: aGroup.classId };
    return p;
  });
  let t = syncGroupPairs({ ...tournament, pairs: newPairs });
  t = rebuildGroupMatches(t, [aGroup, bGroup]);
  return t;
}

export function syncGroupPairs(tournament: any) {
  const groups = tournament.groups.map((g) => ({
    ...g,
    pairs: tournament.pairs.filter((p) => p.groupId === g.id && p.divisionId === g.divisionId),
  }));
  return { ...tournament, groups };
}

export function rebuildGroupMatches(tournament: any, affectedGroups: any, effects?: EngineEffects) {
  const keyOf = (divId, grpId) => `${divId}::${grpId}`;
  const keys = new Set(
    (affectedGroups || []).filter(Boolean).map((g) => keyOf(g.divisionId, g.id)),
  );
  const remaining = tournament.matches.filter(
    (m) => !(m.phase === 'group' && keys.has(keyOf(m.divisionId, m.groupId))),
  );
  const fresh = [];
  tournament.groups.forEach((g) => {
    if (keys.has(keyOf(g.divisionId, g.id))) fresh.push(...buildGroupMatches(g, effects));
  });
  return { ...tournament, matches: [...remaining, ...fresh] };
}

export function unassignPairInTournament(tournament: any, pair: any) {
  if (!tournament || !pair || !pair.groupId) return null;
  const fromGroup = { id: pair.groupId, divisionId: pair.divisionId };
  const newPairs = tournament.pairs.map((p) => (p.id === pair.id ? { ...p, groupId: null } : p));
  let t = syncGroupPairs({ ...tournament, pairs: newPairs });
  t = rebuildGroupMatches(t, [fromGroup]);
  return t;
}

export function findDuplicatePlayerInDivision(tournament: any, pair: any, targetDivisionId: any) {
  if (!tournament || !pair || !targetDivisionId) return null;
  const ids = [pair.p1Id, pair.p2Id].filter(Boolean);
  const namesById = { [pair.p1Id]: pair.p1Name, [pair.p2Id]: pair.p2Name };
  for (const other of tournament.pairs) {
    if (other.id === pair.id) continue;
    if (other.divisionId !== targetDivisionId) continue;
    if (ids.includes(other.p1Id)) return namesById[other.p1Id] || other.p1Name;
    if (ids.includes(other.p2Id)) return namesById[other.p2Id] || other.p2Name;
  }
  return null;
}

export function assignPairToGroup(tournament: any, pair: any, targetGroup: any) {
  if (!tournament || !pair || !targetGroup) return null;
  // Match the target group division-aware (IDs collide across classes)
  const grp = tournament.groups.find(
    (g) => g.id === targetGroup.id && g.divisionId === targetGroup.divisionId,
  );
  if (!grp || grp.divisionId !== pair.divisionId) return null;
  // Don't allow the same player more than once in the same class
  if (findDuplicatePlayerInDivision(tournament, pair, grp.divisionId)) return null;
  const newPairs = tournament.pairs.map((p) =>
    p.id === pair.id
      ? { ...p, groupId: grp.id, divisionId: grp.divisionId, classId: grp.classId }
      : p,
  );
  let t = syncGroupPairs({ ...tournament, pairs: newPairs });
  t = rebuildGroupMatches(t, [grp]);
  return t;
}

export function movePairInTournament(tournament: any, pair: any, targetGroup: any) {
  if (!tournament || !pair || !targetGroup) return null;
  // Match the target group division-aware (IDs collide across classes)
  const grp = tournament.groups.find(
    (g) => g.id === targetGroup.id && g.divisionId === targetGroup.divisionId,
  );
  if (!grp) return null;
  // Same group (same id AND division)? No move.
  if (pair.groupId === grp.id && pair.divisionId === grp.divisionId) return null;
  // When moving to a DIFFERENT class: don't allow it if one of the players is
  // already in the target class (would put the same player twice in the class).
  if (
    grp.divisionId !== pair.divisionId &&
    findDuplicatePlayerInDivision(tournament, pair, grp.divisionId)
  )
    return null;
  // Allow moving to another class (division): the pair inherits the target
  // division's classId/divisionId. The UI warns about this before the call.
  const fromGroup = { id: pair.groupId, divisionId: pair.divisionId };
  const newPairs = tournament.pairs.map((p) =>
    p.id === pair.id
      ? { ...p, groupId: grp.id, divisionId: grp.divisionId, classId: grp.classId }
      : p,
  );
  let t = syncGroupPairs({ ...tournament, pairs: newPairs });
  t = rebuildGroupMatches(t, [fromGroup, grp]);
  return t;
}

export function addGroupInTournament(
  tournament: any,
  classId: any,
  divisionId: any,
  effects?: EngineEffects,
) {
  if (!tournament) return null;
  const { now } = resolveEffects(effects);

  const divGroups = tournament.groups
    .filter((g) => g.divisionId === divisionId)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const usedLetters = new Set(divGroups.map((g) => g.name));
  let nextLetter = null;
  for (let i = 0; i < 26; i++) {
    const letter = String.fromCharCode(65 + i);
    if (!usedLetters.has(letter)) {
      nextLetter = letter;
      break;
    }
  }
  if (!nextLetter) return null;

  const newGroup = {
    id: `grp_${classId}_${divisionId}_${nextLetter.toLowerCase()}_${now}`,
    classId,
    divisionId,
    name: nextLetter,
    pairs: [],
  };

  return { ...tournament, groups: [...tournament.groups, newGroup] };
}

// ── GROUP NAMES ARE LETTERS (#882) ───────────────────────────────────
//
// This renames the survivors with LETTERS — `String.fromCharCode(65 + i)` — the
// same convention every seeder in km.ts now uses (the CTO decided letters,
// everywhere, on 2026-10-18). Before #882 the seeders wrote NUMBERS, so a
// tournament that had a group deleted could mix the two, and
// `rebuildKnockoutForDivision` sorts by `name.localeCompare`, where digits order
// before letters. A tournament drawn before #882 keeps its numbers: a group ADDED
// to it gets the first free letter, which sorts after the digits (last, where a
// new group belongs), and a DELETE renames every survivor here, in their
// existing order — so neither edit reorders the knockout.
export function deleteGroupInTournament(tournament: any, group: any) {
  if (!tournament || !group) return null;

  const divGroups = tournament.groups
    .filter((g) => g.divisionId === group.divisionId)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  if (divGroups.length <= 1) return null;

  const survivingGroups = divGroups.filter((g) => g.id !== group.id);
  const renamedGroups = survivingGroups.map((g, i) => ({
    ...g,
    name: String.fromCharCode(65 + i),
  }));

  const orphanedPairIds = new Set(group.pairs.map((p) => p.id));
  const newPairs = tournament.pairs.map((p) =>
    orphanedPairIds.has(p.id) ? { ...p, groupId: null } : p,
  );

  const otherDivGroups = tournament.groups.filter((g) => g.divisionId !== group.divisionId);

  // Remove the matches from the deleted group + update groupName on the renamed ones
  const newMatches = tournament.matches
    .filter((m) => !(m.phase === 'group' && m.groupId === group.id))
    .map((m) => {
      const renamed = renamedGroups.find((g) => g.id === m.groupId);
      return renamed ? { ...m, groupName: renamed.name } : m;
    });

  return {
    ...tournament,
    pairs: newPairs,
    groups: [...otherDivGroups, ...renamedGroups],
    matches: newMatches,
  };
}

// DELIBERATELY RANDOM (#203 leaves the SCORES alone): this invents a plausible
// scoreline for the admin "fill in random results" button. Randomness is the
// entire point, and it never runs on the #183 server path.
export function kmRandomMatchScore() {
  const team1Stronger = Math.random() < 0.5;

  // Helper: a single set score
  const oneSet = (favoredWins) => {
    // Realistic tennis set: 6-0..6-4, 7-5, 7-6
    const winScore = 6;
    const possibleLoserScores = [0, 1, 2, 3, 4, 4]; // 4 is weighted higher
    const loserScore = possibleLoserScores[Math.floor(Math.random() * possibleLoserScores.length)];
    // 20% chance of 7-5 or 7-6 (close sets)
    if (Math.random() < 0.2) {
      const tight = Math.random() < 0.5 ? [7, 5] : [7, 6];
      return favoredWins ? { t1: tight[0], t2: tight[1] } : { t1: tight[1], t2: tight[0] };
    }
    return favoredWins ? { t1: winScore, t2: loserScore } : { t1: loserScore, t2: winScore };
  };

  // Set 1
  const set1Team1Wins = team1Stronger ? Math.random() < 0.65 : Math.random() < 0.35;
  const set1 = oneSet(set1Team1Wins);

  // Set 2
  const set2Team1Wins = team1Stronger ? Math.random() < 0.65 : Math.random() < 0.35;
  const set2 = oneSet(set2Team1Wins);

  // If 1-1: super-tiebreak to 10
  if (set1Team1Wins !== set2Team1Wins) {
    const stbTeam1Wins = team1Stronger ? Math.random() < 0.55 : Math.random() < 0.45;
    // Super-TB: winner 10, loser 0-8 (most often 6-9)
    const loserPossible = [3, 4, 5, 6, 6, 7, 7, 8, 8];
    const loserStb = loserPossible[Math.floor(Math.random() * loserPossible.length)];
    const stb = stbTeam1Wins ? { t1: 10, t2: loserStb } : { t1: loserStb, t2: 10 };
    const sets = [set1, set2, stb];
    const t1Sets = sets.filter((s) => s.t1 > s.t2).length;
    const t2Sets = sets.filter((s) => s.t2 > s.t1).length;
    return { t1: t1Sets, t2: t2Sets, sets };
  }

  // 2-0 or 0-2
  const sets = [set1, set2];
  const t1Sets = sets.filter((s) => s.t1 > s.t2).length;
  const t2Sets = sets.filter((s) => s.t2 > s.t1).length;
  return { t1: t1Sets, t2: t2Sets, sets };
}

export function applyKMRandomResults(
  tournament: any,
  matchFilter: any = () => true,
  allPlayers: any = [],
  effects?: EngineEffects,
) {
  if (!tournament?.matches) return tournament;
  // The SCORES stay random — that is what this function is for. The timestamp
  // is not part of that purpose, and it previously read the clock once per
  // filled match, so a bulk fill stamped matches with times that drifted apart
  // as the loop ran. One instant for the whole fill (#203), and one settings
  // snapshot for the whole fill too (#243).
  const { now, settings } = resolveEffects(effects);

  const newMatches = tournament.matches.map((m) => {
    if (m.score) return m; // already finished
    if (!m.team1?.pairId || !m.team2?.pairId) return m; // missing concrete pairs
    if (!matchFilter(m)) return m; // filter said no

    const score = kmRandomMatchScore();
    return { ...m, score, completedAt: now };
  });

  let newTournament = { ...tournament, matches: newMatches };

  // Store base ELO on the first result (same as handleSaveScore)
  // `playerBaseElo` IS NO LONGER CAPTURED (#249).
  //
  // It froze each player's rating at the first result and then decided the
  // whole season's calculations, so a player who improved was still measured
  // from where they started. The rule is now the player's current
  // authoritative rating at the moment each result is recorded.
  //
  // Existing tournaments keep the field: it is historical data, and
  // `SeriespillPlayerModal` still reads it for the season's starting point
  // (falling back to `playerElo − eloDelta` where it is absent).

  // Chronological ELO recalc — so that random-filled matches also show the ELO change
  try {
    const { matches: eloMatches } = recalcKMElo(newTournament, allPlayers, settings);
    newTournament = { ...newTournament, matches: eloMatches };
  } catch (cause) {
    // Explicitly lenient: this is the admin "fill with random results" button,
    // and a warning on the console is the right answer for a demo tool.
    warnOnKMIssue({ step: 'elo-recalc', cause });
  }

  // After the score update: run propagation so group winners fill the KO placeholders
  try {
    propagateKMResults(newTournament);
  } catch (cause) {
    warnOnKMIssue({ step: 'propagation', cause });
  }

  return newTournament;
}

export function recalcKMElo(
  tournament: any,
  allPlayers: any,
  settings: EngineSettingsValues = EngineSettings,
) {
  // ELO weight: configurable per tournament (as in Padel24). Default 2.0 (200%)
  // — club championships are weighted more heavily than ordinary tournaments.
  const KM_MULTIPLIER = tournament.config?.kMultiplier ?? 2.0;
  // Per-match ELO weight: if the match's class overrides kMultiplier, that one is used.
  // Otherwise the series default. Safe for KM (classes without an override → same as before).
  const multForMatch = (m) => {
    if (m?.classId && Array.isArray(tournament.config?.classes)) {
      const cls = tournament.config.classes.find((c) => c.id === m.classId);
      if (cls && cls.kMultiplier != null) return cls.kMultiplier;
    }
    return KM_MULTIPLIER;
  };
  const matches = (tournament.matches || []).map((m) => ({ ...m }));
  const pairs = tournament.pairs || [];
  const pairById = new Map(pairs.map((p) => [p.id, p]));
  // Division-scoped pair lookup: old series data can have duplicate pair IDs
  // across divisions, so a global Map returns the wrong pair (and thus the
  // wrong player IDs). The running ELO then isn't accumulated on the right team, and
  // every round shows base ELO instead of the updated ELO. Look up by
  // id + divisionId first, fall back to global.
  const pairLookup = (pairId, divisionId) => {
    if (!pairId) return null;
    if (divisionId != null) {
      const scoped = pairs.find((p) => p.id === pairId && p.divisionId === divisionId);
      if (scoped) return scoped;
    }
    return pairById.get(pairId) || null;
  };
  // Robust player-ID derivation for a team: roster → the match's own p*Id → pair.
  // Makes recalcKMElo immune to pair-id collisions and orphaned pair IDs.
  const teamIds = (roster, team, pair) => {
    if (Array.isArray(roster) && roster.filter(Boolean).length === 2) return roster.filter(Boolean);
    if (team?.p1Id && team?.p2Id) return [team.p1Id, team.p2Id];
    if (pair?.p1Id && pair?.p2Id) return [pair.p1Id, pair.p2Id];
    return [];
  };

  // THE PLAYER'S CURRENT AUTHORITATIVE RATING (#249).
  //
  // This used to prefer `tournament.playerBaseElo` — a baseline frozen at the
  // first result and then used for the whole season, so a player who improved
  // was still calculated from where they started. The CTO's rule is that a
  // result uses the player's actual, latest rating at the moment it is
  // recorded, and that a season's ELO follows the player's development.
  //
  // `playerBaseElo` is no longer read. It is still WRITTEN on nothing new, and
  // still present on older tournaments, where `SeriespillPlayerModal` reads it
  // for the season's starting point.
  const baseElo = {};
  const getBase = (pid) => {
    if (baseElo[pid] != null) return baseElo[pid];
    const pl = (allPlayers || []).find((p) => p.id === pid);
    baseElo[pid] = pl?.elo ?? 1500;
    return baseElo[pid];
  };

  // Running ELO per player
  const liveElo = {};
  const eloOf = (pid) => (liveElo[pid] != null ? liveElo[pid] : (liveElo[pid] = getBase(pid)));

  // Played matches in REGISTRATION ORDER (completedAt) — each match's starting ELO
  // is then the rating after the previous registered match, so that later adjustments
  // come out correct. Walkover matches are kept out of ELO (no real match was played).
  const played = matches
    .filter((m) => m.score && !m.score.walkover && m.team1?.pairId && m.team2?.pairId)
    .map((m, i) => ({ m, i }))
    .sort((x, y) => {
      const a = x.m,
        b = y.m;
      const ca = a.completedAt || 0,
        cb = b.completedAt || 0;
      if (ca !== cb) return ca - cb;
      // Fallback (old data without completedAt): scheduled date + start time, then original order.
      const ad = a.date || '',
        bd = b.date || '';
      if (ad !== bd) return ad.localeCompare(bd);
      const sa = (a.startTime || '').localeCompare(b.startTime || '');
      if (sa !== 0) return sa;
      return x.i - y.i;
    })
    .map((x) => x.m);

  played.forEach((m) => {
    // ── A RECORDED CONTRIBUTION IS A HISTORICAL FACT (#249, rules 3-4) ──
    //
    // It is never recomputed. Not when a later result arrives, not when one is
    // entered out of order, and not when an earlier match is corrected — the
    // three cases where the chronological replay used to rewrite a neighbour
    // (measured: 45 → 40 out of order, 40 → 51 on a correction).
    //
    // This covers matches recorded under the OLD semantics too, and that is
    // deliberate: their `teamEloChange` is just as much a historical fact, and
    // rule 4 says nothing recalculates them.
    //
    // It is also what makes the running rating below equal the player's
    // current authoritative one: nothing accumulates, because nothing replays.
    if (m.teamEloChange != null) return;

    const p1 = pairLookup(m.team1.pairId, m.divisionId);
    const p2 = pairLookup(m.team2.pairId, m.divisionId);
    // Player IDs are taken primarily from the match itself (roster/team*.p*Id) — the series
    // stores them directly — so that a pair-id collision or orphaned pair IDs
    // don't corrupt the accumulation. The pair lookup is only the last fallback (KM).
    const t1Players = teamIds(m.roster?.t1, m.team1, p1);
    const t2Players = teamIds(m.roster?.t2, m.team2, p2);
    if (t1Players.length !== 2 || t2Players.length !== 2) return;

    // The team's (current) average ELO — as we do it elsewhere in the system
    const t1Avg = (eloOf(t1Players[0]) + eloOf(t1Players[1])) / 2;
    const t2Avg = (eloOf(t2Players[0]) + eloOf(t2Players[1])) / 2;

    const s1 = m.score.t1,
      s2 = m.score.t2;
    const t1Result = s1 > s2 ? 'win' : s1 < s2 ? 'loss' : 'draw';
    const t2Result = s2 > s1 ? 'win' : s2 < s1 ? 'loss' : 'draw';
    const mov = typeof kmMatchMov === 'function' ? kmMatchMov(m.score, settings) : 1;
    const mMult = multForMatch(m);
    const t1Change = calcEloChange(t1Avg, t2Avg, t1Result, mov, mMult, 1, settings);
    const t2Change = calcEloChange(t2Avg, t1Avg, t2Result, mov, mMult, 1, settings);

    // WHO THIS CONTRIBUTION BELONGS TO, stored with it (#249, rule 2).
    //
    // `teamEloChange` is per TEAM; committing it to players needs the two
    // rosters, and they are derived here from roster → the match's own p*Id →
    // the pair, in that order. Recording the answer means the commit uses the
    // exact players that were rated, rather than re-deriving them later from a
    // pair lookup that may resolve differently (duplicate pair ids across
    // divisions are real in old series data — see `pairLookup` above).
    m.eloRoster = { t1: [...t1Players], t2: [...t2Players] };
    // Store pre/post + the change on the match (average-based values)
    m.playerEloPre = {
      [t1Players[0]]: eloOf(t1Players[0]),
      [t1Players[1]]: eloOf(t1Players[1]),
      [t2Players[0]]: eloOf(t2Players[0]),
      [t2Players[1]]: eloOf(t2Players[1]),
    };
    m.teamEloPre = { t1: Math.round(t1Avg), t2: Math.round(t2Avg) };
    m.teamEloChange = { t1: t1Change, t2: t2Change };

    // Update the running ELO (both players on the team get the same change)
    t1Players.forEach((pid) => {
      liveElo[pid] = eloOf(pid) + t1Change;
    });
    t2Players.forEach((pid) => {
      liveElo[pid] = eloOf(pid) + t2Change;
    });

    m.teamEloPost = {
      t1: Math.round((eloOf(t1Players[0]) + eloOf(t1Players[1])) / 2),
      t2: Math.round((eloOf(t2Players[0]) + eloOf(t2Players[1])) / 2),
    };
  });

  // For unplayed matches: show the team's CURRENT average ELO (after all played
  // matches so far), but no change. This way an upcoming match shows the correct
  // starting point — e.g. 2787 after two losses, not the static starting ELO.
  // Use chronological positioning: a team's ELO in an upcoming match reflects
  // the results BEFORE that match in time.
  // THE PLAYERS' CURRENT RATING, not a replay of the season (#249).
  //
  // Every played match's contribution is committed as it is recorded, so the
  // authoritative rating already contains it; adding the changes again here
  // would show an upcoming match a rating nobody holds. `liveAfter` therefore
  // stays at the current value and the accumulation below is gone.
  const liveAfter = { ...baseElo };
  // Build a timeline over all matches (played + unplayed) chronologically, and
  // track the running ELO. Played matches use the already-computed changes.
  const chronological = matches
    .filter((m) => m.team1?.pairId && m.team2?.pairId)
    .sort((a, b) => {
      // Same chronology as the ELO calculation itself: registration order
      // (completedAt). Played matches first in registration order; unplayed
      // last (Infinity), so that an UPCOMING match shows the team's CURRENT ELO
      // after ALL played matches — same behaviour as P24 (a new match = updated
      // ELO after the last registered result), not round-positional ELO.
      const ca = a.completedAt || Infinity,
        cb = b.completedAt || Infinity;
      if (ca !== cb) return ca - cb;
      const ad = a.date || '\uffff',
        bd = b.date || '\uffff';
      if (ad !== bd) return ad.localeCompare(bd);
      return (a.startTime || '\uffff').localeCompare(b.startTime || '\uffff');
    });
  const eloNow = (pid) =>
    liveAfter[pid] != null ? liveAfter[pid] : (liveAfter[pid] = getBase(pid));
  chronological.forEach((m) => {
    const p1 = pairLookup(m.team1.pairId, m.divisionId);
    const p2 = pairLookup(m.team2.pairId, m.divisionId);
    const t1P = teamIds(m.roster?.t1, m.team1, p1);
    const t2P = teamIds(m.roster?.t2, m.team2, p2);
    if (t1P.length !== 2 || t2P.length !== 2) return;

    if (m.score) {
      // Played: nothing to accumulate. Its contribution is already in the
      // authoritative rating `eloNow` reads.
      t1P.concat(t2P).forEach((pid) => eloNow(pid));
    } else {
      // Unplayed match: set pre-ELO to the team's current average, no change
      m.playerEloPre = {
        [t1P[0]]: eloNow(t1P[0]),
        [t1P[1]]: eloNow(t1P[1]),
        [t2P[0]]: eloNow(t2P[0]),
        [t2P[1]]: eloNow(t2P[1]),
      };
      m.teamEloPre = {
        t1: Math.round((eloNow(t1P[0]) + eloNow(t1P[1])) / 2),
        t2: Math.round((eloNow(t2P[0]) + eloNow(t2P[1])) / 2),
      };
      m.teamEloChange = null;
      m.teamEloPost = null;
    }
  });

  // Matches without concrete teams (placeholders) or outside the chronology: reset
  matches.forEach((m) => {
    if (!m.score && !m.teamEloPre) {
      m.playerEloPre = null;
      m.teamEloPre = null;
      m.teamEloChange = null;
      m.teamEloPost = null;
    }
  });

  // `finalElo` / `baseElo` are what THIS CALL computed, and since #249 that is
  // only the matches it actually calculated — a recorded contribution is
  // skipped, so a recalc over a fully-recorded division returns empty maps.
  // Nothing in the product reads either field; they are returned for tests and
  // for a caller that wants to see the post-match ratings of the result it
  // just recorded.
  return { matches, finalElo: { ...liveElo }, baseElo: { ...baseElo } };
}

export function propagateKMResults(tournament: any) {
  if (!tournament?.config?.classes) return;

  tournament.config.classes.forEach((cls) => {
    cls.divisions.forEach((div) => {
      const divGroups = tournament.groups.filter((g) => g.divisionId === div.id);
      const divGroupMatches = tournament.matches.filter(
        (m) => m.divisionId === div.id && m.phase === 'group',
      );
      const divKoMatches = tournament.matches.filter(
        (m) => m.divisionId === div.id && m.phase === 'knockout',
      );

      // For each group: if all matches are played, compute standings and fill the placeholders
      const allGroupsComplete = divGroups.every((g) => {
        const grpMatches = divGroupMatches.filter((m) => m.groupId === g.id);
        return grpMatches.length > 0 && grpMatches.every((m) => m.score);
      });

      // Per group: fill the placeholders based on standings (even if the whole division isn't finished)
      divGroups.forEach((g) => {
        const grpMatches = divGroupMatches.filter((m) => m.groupId === g.id);
        const allPlayed = grpMatches.length > 0 && grpMatches.every((m) => m.score);
        if (!allPlayed) return;

        const standings = calcGroupStandings(g.pairs, grpMatches, tournament.config?.rankingOrder);

        // Fill all KO placeholders that point to this group
        divKoMatches.forEach((m) => {
          for (const slot of ['team1', 'team2']) {
            const t = m[slot];
            if (t?.from === 'group' && t.groupId === g.id && !t.pairId) {
              const standing = standings.find((s) => s.pos === t.pos);
              if (standing) {
                m[slot] = { ...t, pairId: standing.pair.id };
              }
            }
          }
        });
      });

      // If all groups are finished + there are lucky losers — compute the LL ranking
      if (allGroupsComplete && (div.luckyLosers || 0) > 0) {
        // Gather the LL candidates (the first non-directly-qualified from each group),
        // and keep the group's full standings + matches for a fair comparison.
        const llCandidates = [];
        divGroups.forEach((g) => {
          const grpMatches = divGroupMatches.filter((m) => m.groupId === g.id);
          const standings = calcGroupStandings(
            g.pairs,
            grpMatches,
            tournament.config?.rankingOrder,
          );
          const ll = standings.find((s) => s.pos === (div.advanceTopN || 1) + 1);
          if (ll)
            llCandidates.push({
              ...ll,
              groupSize: g.pairs.length,
              groupStandings: standings,
              groupMatches: grpMatches,
            });
        });

        // ── The FIFA/UEFA method for a fair comparison across uneven
        // groups: when the groups are of different sizes, we disregard each
        // candidate's result against the WEAKEST teams in their own group, so that
        // all candidates are compared over the same number of matches (= smallest group − 1).
        const minGroupSize = Math.min(...llCandidates.map((c) => c.groupSize));
        const adjustForFairness = (cand) => {
          const stripCount = cand.groupSize - minGroupSize; // how many of the weakest to disregard
          const adj = {
            points: cand.points,
            setDiff: cand.setsFor - cand.setsAgainst,
            gameDiff: cand.gamesFor - cand.gamesAgainst,
            played: cand.played,
            wins: cand.wins,
          };
          if (stripCount > 0) {
            // The weakest teams in the group (lowest placement), exclude the candidate itself
            const weakest = cand.groupStandings
              .filter((s) => s.pair.id !== cand.pair.id)
              .slice(-stripCount)
              .map((s) => s.pair.id);
            const weakSet = new Set(weakest);
            // Subtract the candidate's matches against the weakest
            (cand.groupMatches || []).forEach((m) => {
              if (!m.score) return;
              const isT1 = m.team1?.pairId === cand.pair.id;
              const isT2 = m.team2?.pairId === cand.pair.id;
              if (!isT1 && !isT2) return;
              const oppId = isT1 ? m.team2?.pairId : m.team1?.pairId;
              if (!weakSet.has(oppId)) return;
              // Remove this result from the candidate's adjusted stats
              const my = isT1 ? m.score.t1 : m.score.t2;
              const opp = isT1 ? m.score.t2 : m.score.t1;
              adj.played -= 1;
              if (my > opp) {
                adj.points -= 3;
                adj.wins -= 1;
              }
              adj.setDiff -= my - opp;
              if (Array.isArray(m.score.sets)) {
                m.score.sets.forEach((set) => {
                  const mg = isT1 ? set.t1 || 0 : set.t2 || 0;
                  const og = isT1 ? set.t2 || 0 : set.t1 || 0;
                  adj.gameDiff -= mg - og;
                });
              }
            });
          }
          return adj;
        };
        llCandidates.forEach((c) => {
          c._fair = adjustForFairness(c);
        });

        // Sort the LL candidates on the adjusted (fair) numbers — now they all
        // have the same number of counting matches, so we can use totals directly.
        llCandidates.sort((a, b) => {
          if (b._fair.points !== a._fair.points) return b._fair.points - a._fair.points;
          if (b._fair.setDiff !== a._fair.setDiff) return b._fair.setDiff - a._fair.setDiff;
          if (b._fair.gameDiff !== a._fair.gameDiff) return b._fair.gameDiff - a._fair.gameDiff;
          // Final tiebreak: total games-for per match (unchanged)
          const gpmA = a.played > 0 ? a.gamesFor / a.played : 0;
          const gpmB = b.played > 0 ? b.gamesFor / b.played : 0;
          return gpmB - gpmA;
        });

        // Fill the LL placeholders
        divKoMatches.forEach((m) => {
          for (const slot of ['team1', 'team2']) {
            const t = m[slot];
            if (t?.from === 'lucky_loser' && !t.pairId) {
              const ll = llCandidates[t.llRank - 1];
              if (ll) {
                m[slot] = { ...t, pairId: ll.pair.id };
              }
            }
          }
        });
      }

      // ──── A-bracket progression: fill in winners/losers from A-bracket rounds ────
      // Walk through the KO matches in round order, and fill in placeholders that point back
      // (the original carried an unused `roundOrder` list here — dropped, dead code; PR-AT)

      // Build a lookup of KO matches per (round, slot)
      const koLookup = {};
      divKoMatches.forEach((m) => {
        if (m.bracket === 'A' && m.round && m.slot) {
          koLookup[`${m.round}_${m.slot}`] = m;
        }
      });

      // For each A-bracket round: if the match has a result, fill the winner/loser into the next round
      divKoMatches.forEach((srcMatch) => {
        if (srcMatch.bracket !== 'A') return;
        if (!srcMatch.score) return;
        const winnerPairId =
          srcMatch.score.t1 > srcMatch.score.t2 ? srcMatch.team1?.pairId : srcMatch.team2?.pairId;
        const loserPairId =
          srcMatch.score.t1 > srcMatch.score.t2 ? srcMatch.team2?.pairId : srcMatch.team1?.pairId;
        if (!winnerPairId) return;

        // Find matches that point to this one (with from: 'a_winner' or 'a_loser')
        divKoMatches.forEach((target) => {
          for (const slot of ['team1', 'team2']) {
            const t = target[slot];
            if (!t || t.pairId) continue;
            if (
              t.from === 'a_winner' &&
              t.prevRound === srcMatch.round &&
              t.prevSlot === srcMatch.slot
            ) {
              target[slot] = { ...t, pairId: winnerPairId };
            }
            if (
              t.from === 'a_loser' &&
              t.prevRound === srcMatch.round &&
              t.prevSlot === srcMatch.slot &&
              loserPairId
            ) {
              target[slot] = { ...t, pairId: loserPairId };
            }
          }
        });
      });

      // ──── B-bracket progression: losers of the A-bracket → B-bracket first round, then B winners ────
      divKoMatches.forEach((srcMatch) => {
        if (!srcMatch.score) return;
        const winnerPairId =
          srcMatch.score.t1 > srcMatch.score.t2 ? srcMatch.team1?.pairId : srcMatch.team2?.pairId;
        const loserPairId =
          srcMatch.score.t1 > srcMatch.score.t2 ? srcMatch.team2?.pairId : srcMatch.team1?.pairId;
        if (!winnerPairId || !loserPairId) return;

        // B-bracket: 'a_loser' refers to the losers of an A-bracket round
        if (srcMatch.bracket === 'A') {
          divKoMatches.forEach((target) => {
            if (target.bracket !== 'B') return;
            for (const slot of ['team1', 'team2']) {
              const t = target[slot];
              if (!t || t.pairId) continue;
              if (
                t.from === 'a_loser' &&
                t.aRound === srcMatch.round &&
                t.aSlot === srcMatch.slot
              ) {
                target[slot] = { ...t, pairId: loserPairId };
              }
            }
          });
        }

        // B-bracket winner progression
        if (srcMatch.bracket === 'B') {
          divKoMatches.forEach((target) => {
            if (target.bracket !== 'B') return;
            for (const slot of ['team1', 'team2']) {
              const t = target[slot];
              if (!t || t.pairId) continue;
              if (
                t.from === 'b_winner' &&
                t.bRound === srcMatch.round &&
                t.bSlot === srcMatch.slot
              ) {
                target[slot] = { ...t, pairId: winnerPairId };
              }
              // B bronze final: the losers of the B semifinals
              if (
                t.from === 'b_loser' &&
                t.bRound === srcMatch.round &&
                t.bSlot === srcMatch.slot
              ) {
                target[slot] = { ...t, pairId: loserPairId };
              }
            }
          });
        }
      });
    });
  });
}
